import { DefaultButton, DetailsList, IColumn, Icon, Link, SelectionMode, Spinner } from '@fluentui/react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { EntityAPI, EntityType, FieldType, IContentMetadataField } from '../../../API';
import { IUserConfigPackage } from '../../../API';
import ContentMetadataFieldEditor, { ContentMetadataFieldState } from './ContentMetadataFieldEditor';
import { InfoTooltipLabel } from '../../controls/InfoTooltipLabel';

export class ContentMetadataFieldsListState {
	
	public userConfig: IUserConfigPackage;

	public loading: boolean = false;
	public loaded: boolean = false;

	public editState: ContentMetadataFieldState;

	public ids: string[] = [];
	
	update?: (ids: string[]) => void;
	
	items: IContentMetadataField[] = [];

	constructor(userConfig: IUserConfigPackage, update?:(ids:string[]) => void) {
		this.userConfig = userConfig;
		this.update = update;
		
		this.editState = new ContentMetadataFieldState(this.userConfig, this.delete.bind(this), this.save.bind(this));

		makeAutoObservable(this);
	}
	
	public availableContentHubColumns: string[] = [];
	public setAvailableContentHubColumns(columns: string[]) {
		this.availableContentHubColumns = columns;
	}
	
	public setSelected(ids:string[]) {
		this.ids = ids || [];
	}
	
	public async load(): Promise<void> {
		this.loading = true;

		if(this.ids.length > 0) {
			let api = await this.getApi();
			let items = await api.items(this.ids, null);
			this.setItems(items);
		} else {
			this.setItems([]);
		}
	}

	private async getApi() : Promise<EntityAPI<IContentMetadataField>> {
		return await EntityAPI.fromEntityType(
			EntityType.ContentMetadataField,
			this.userConfig,
		) as EntityAPI<IContentMetadataField>;
	}

	ensureInitialLoaded() {
		if (!this.loading && !this.loaded) {
			this.load();
		}
	}

	setItems(items: IContentMetadataField[]) {
		this.loading = false;
		this.loaded = true;
		this.items = items;
	}
	
	edit(item: IContentMetadataField) {
		if(this.editState) {
			this.editState.setItem(item);
			this.editState.dialog.setVisible(true);
			this.editState.setAvailableContentHubColumns(this.availableContentHubColumns);
		}
	}
	
	public showAddNewDialog() {

		this.edit({
			ValueFromTokenizedValue: '',
			CreateFieldIfMissing: false,
			CreateFieldCustomFieldType: FieldType.Text,
			DestinationFieldInternalName: '',
			CreateFieldDisplayName: '',

			Id: '',
			DisplayTitle: '',
			ServerRelativeUrl: '',
			NativeMetadataUrl: '',
		});
	}

	public async save(original:IContentMetadataField, updated:IContentMetadataField) : Promise<void> {
		
		//Update the original with the new properties
		Object.assign(original, updated);

		if(!original.Id) {
			//There was no id - it needs added to the list
			this.items.push(original);
		}

		let api = await this.getApi();
		original.Id = await api.upsert(updated, []);

		this.updated();
	}

	public async delete(item:IContentMetadataField) {
		this.remove(item);
		let api = await this.getApi();
		await api.delete([item.Id]);
	}

	public remove(item:IContentMetadataField) {
		this.items = this.items.filter(p => p !== item);
		this.updated();
	}
	
	public updated() {
		//Force a rerender
		this.items = this.items.slice();

		//Notify of new values
		if(this.update) {
			this.update(this.items.map(p => p.Id));
		}
	}
}

function ContentMetadataFieldsList({ state }: { state: ContentMetadataFieldsListState }) {
	useEffect(() => {
		state.ensureInitialLoaded();
	}, [state]);

	let columns: IColumn[] = [
		{
			key: 'field',
			name: 'Source value',
			minWidth: 150,
			maxWidth: 150,
			onRender: (item: IContentMetadataField) => <Link onClick={() => {
				state.edit(item);
			}}>{item.ValueFromTokenizedValue}</Link>,
		},
		{
			key: 'value',
			name: 'Destination field',
			minWidth: 150,
			maxWidth: 150,
			onRender: (item: IContentMetadataField) => <>{item.DestinationFieldInternalName}</>,
		},
		{
			key: 'delete',
			name: '',
			minWidth: 100,
			onRender: (item: IContentMetadataField) => <Icon style={{fontSize:20, cursor:'pointer'}} iconName={'Delete'} onClick={() => { state.delete(item) }} />,
		}
	];


	if (state.loading) {
		return <Spinner></Spinner>;
	}
	
	return (
		<>
	
			{state.editState && <ContentMetadataFieldEditor state={state.editState} /> }
			
			<InfoTooltipLabel label='Content metadata default values' title='Content metadata fields can be mapped from tokens or static values and will be applied to the content via location based default values.'>

				{(state?.items && state.items.length) ?
					<DetailsList selectionMode={SelectionMode.none} columns={columns} items={state?.items || []} /> : 
					<></>
				}
					
				<div style={{marginTop:20}}>
					<DefaultButton onClick={state.showAddNewDialog.bind(state)}>Add</DefaultButton>
				</div>
			</InfoTooltipLabel>


		</>
	);
}

export default observer(ContentMetadataFieldsList);
